import {
  InvoiceGroupInvoiceDto,
  InvoiceGroupStatus,
  InvoiceGroupType
} from '../payments/invoice-group.dto';
import { InvoiceStatus } from '../payments/invoices.dto';

/** Which volume band a cycle landed in. `None` is the free tier — no shifts, nothing owed. */
export enum SubscriptionTier {
  None = 'None',
  Tier1 = 'Tier1',
  Tier2 = 'Tier2',
  Tier3 = 'Tier3',
  Tier4 = 'Tier4'
}

/**
 * One volume-based pricing band for a facility subscription.
 *
 * Bands are flat monthly prices resolved from the cycle's countable shift
 * count — not per-shift and not graduated (Specification v2 §3).
 *
 * Served by the API on the open cycle rather than mirrored here: the bands and
 * their prices live in `SubscriptionTierResolver`, and a second copy on this
 * side would drift the moment pricing changes.
 */
export interface SubscriptionTierBand {
  tier: SubscriptionTier;
  tierDisplay: string;
  /** Inclusive lower bound of the band, in shifts. */
  lowerBound: number;
  /**
   * Inclusive upper bound. Absent on the open-ended top band.
   *
   * Optional *and* nullable on purpose: the API is configured to omit null
   * properties, so the top band arrives with no `upperBound` at all rather
   * than an explicit null. Comparing against `null` alone silently misses it.
   */
  upperBound?: number | null;
  /** Pre-formatted by the API, e.g. `51-100` or `101+`. */
  shiftRange: string;
  /** Flat price charged for the cycle when usage lands in this band. */
  price: number;
}

/** Subscription products a facility can activate independently of each other. */
export enum FacilitySubscriptionProduct {
  TemporaryStaffing = 'TemporaryStaffing',
  JobPostings = 'JobPostings'
}

/** Lifecycle of the facility's subscription as a whole. */
export enum FacilitySubscriptionState {
  PendingActivation = 'PendingActivation',
  Active = 'Active',
  GracePeriod = 'GracePeriod',
  Suspended = 'Suspended',
  Cancelled = 'Cancelled'
}

/**
 * How each state is written for facilities and corporate staff alike — the
 * facility's status badge and the corporate status filter name the same states,
 * so they read from one record rather than each spelling them out.
 */
export const FACILITY_SUBSCRIPTION_STATE_LABELS: Record<
  FacilitySubscriptionState,
  string
> = {
  [FacilitySubscriptionState.PendingActivation]: 'Pending activation',
  [FacilitySubscriptionState.Active]: 'Active',
  [FacilitySubscriptionState.GracePeriod]: 'Grace period',
  [FacilitySubscriptionState.Suspended]: 'Suspended',
  [FacilitySubscriptionState.Cancelled]: 'Cancelled'
};

/** Whether a given product is currently active for the facility, and its price. */
export interface FacilitySubscriptionStatus {
  product: FacilitySubscriptionProduct;
  /**
   * Whether the facility is still entitled to the product.
   *
   * Mirrors the API's `isActive`. Removal takes effect at the cycle boundary
   * (spec §8), so a product the facility has just switched off stays entitled —
   * and usable — until then.
   *
   * This is not the switch position: an admin who has switched a product off
   * expects to see it off straight away. Use {@link isSwitchedOn} for that.
   */
  active: boolean;
  /**
   * The instant billing stops, where the product is ending.
   *
   * **Exclusive**, mirroring the cycle end the API takes from Stripe: a cycle
   * closing at the end of August reports midnight on 1 September.
   *
   * Use it to ask *whether* something is ending, or whether that moment has
   * passed. Never to work out *which day* it stops — that is
   * {@link lastCoveredDate}, which the API resolves in the facility's own
   * timezone. Deriving a date here would do it in the viewer's instead.
   */
  endsAt?: string;
  /**
   * The last facility-local **date** the product still covers, as `YYYY-MM-DD`.
   * Absent while nothing is ending.
   *
   * Use this anywhere a date is wanted — a calendar limit, a badge — rather
   * than deriving one from {@link endsAt}. The API resolves it through the same
   * call the backend's boundary sweep cancels from, so a calendar limited to
   * this date cannot offer a day the sweep would then take back. It also
   * carries no time, so it renders the same for every viewer; reading `endsAt`
   * in the browser's zone gets it wrong for anyone east of the facility.
   *
   * `endsAt` is still the right field for *whether* something is ending, and
   * for whether that moment has passed.
   */
  lastCoveredDate?: string;
  /**
   * Flat monthly price for products billed at a fixed rate. Absent for
   * usage-tiered products, which price from `SubscriptionTier[]` instead.
   */
  monthlyPrice?: number;
}

/**
 * The subscription record as the API returns it. Only the parts the apps read
 * are modelled — the rest (Stripe ids, cycle anchors) is not used here.
 */
export interface FacilitySubscriptionResponse {
  status?: FacilitySubscriptionState;
  statusDisplay?: string;
  products?: {
    product: FacilitySubscriptionProduct;
    productDisplay: string;
    isActive: boolean;
    /** Set once removal is scheduled, while `isActive` is still true. */
    deactivatedAt?: string | null;
    /** `YYYY-MM-DD`, resolved by the API from `deactivatedAt` in the facility's zone. */
    lastCoveredDate?: string | null;
  }[];
}

/**
 * A facility's subscription, in the shape the pages read.
 *
 * `null` where the facility has never subscribed — the API answers 404 there,
 * which is the free-tier case rather than a failure.
 */
export interface FacilitySubscriptionSummary {
  /** Lifecycle of the subscription as a whole, behind the status badge. */
  status: FacilitySubscriptionState;
  statusDisplay: string;
  products: FacilitySubscriptionStatus[];
}

/**
 * Reads the product states off a subscription record.
 *
 * Shared because both apps read the same facility route: the corporate rollup
 * reports products as a boolean derived from `isActive`, which stays true
 * through the removal window, so the corporate detail page reads this instead.
 */
export function toSubscriptionSummary(
  subscription: FacilitySubscriptionResponse
): FacilitySubscriptionSummary {
  return {
    // The API omits nulls, so an absent status is the pre-activation state
    // rather than a missing field.
    status: subscription.status ?? FacilitySubscriptionState.PendingActivation,
    statusDisplay: subscription.statusDisplay ?? '',
    products: toSubscriptionProducts(subscription)
  };
}

export function toSubscriptionProducts(
  subscription: FacilitySubscriptionResponse
): FacilitySubscriptionStatus[] {
  return (subscription.products ?? []).map((product) => ({
    product: product.product,
    // Entitlement, not switch position. A product carrying a deactivation date
    // is on its way out but remains usable until it arrives.
    active: product.isActive,
    endsAt: product.deactivatedAt ?? undefined,
    lastCoveredDate: product.lastCoveredDate ?? undefined
  }));
}

/**
 * Where the switch sits for a product.
 *
 * On only while the facility means to keep it: a product inside its removal
 * window is still entitled — {@link FacilitySubscriptionStatus.active} stays
 * true to the cycle boundary — but the switch reads off, because that is what
 * the admin chose. Reading `active` here would spring the switch back on
 * straight after they turned it off.
 */
export function isSwitchedOn(
  status: FacilitySubscriptionStatus | undefined
): boolean {
  return !!status?.active && !status.endsAt;
}

/**
 * Whether the facility may still use the product right now.
 *
 * Removal takes effect at the cycle boundary (spec §8), so a switched-off
 * product stays usable until `endsAt` passes. The date has to be compared
 * rather than trusting the flag: nothing on the backend clears `isActive` when
 * the boundary arrives — it is only cleared when the whole subscription is
 * cancelled — so a product removed months ago still reports `isActive: true`.
 *
 * `endsAt` is exclusive: it is the first instant no longer covered.
 */
export function isEntitled(
  status: FacilitySubscriptionStatus | undefined,
  now: Date = new Date()
): boolean {
  if (!status?.active) return false;

  return !status.endsAt || new Date(status.endsAt) > now;
}

/**
 * The open billing cycle as a live estimate.
 *
 * Always **tax-exclusive** — Stripe Tax only calculates at finalization, so no
 * tax figure exists for a cycle that has not closed.
 */
export interface OpenCycleSubscription {
  facilityId: string;
  facilityName: string;
  /** ISO dates bounding the current cycle. */
  cyclePeriodStart: string;
  cyclePeriodEnd: string;
  /** Pre-formatted by the API, e.g. `1–31 July 2026`. */
  cyclePeriodFormatted: string;
  daysRemaining: number;

  /** Everything scheduled in the cycle — the provisional count. */
  plannedShiftCount: number;
  /** Worked plus facility-initiated late cancellations — what actually bills. */
  actualShiftCount: number;

  /** Where the confirmed count sits today. */
  currentTier: SubscriptionTier;
  currentTierDisplay: string;
  currentTierShiftRange: string;
  currentTierCost: number;

  /** Where the planned count would land it. */
  projectedTier: SubscriptionTier;
  projectedTierDisplay: string;
  projectedTierShiftRange: string;
  projectedTierCost: number;

  /** Flat product fees, prorated when switched on mid-cycle. */
  estimatedProductFees: number;
  estimatedTotalIfCycleClosedNow: number;
  estimatedTotalAtPlannedVolume: number;

  /** Always true; the figures are estimates until the cycle closes. */
  isEstimate: boolean;

  /**
   * The whole ladder in ascending order, so the usage widget can draw its
   * scale and the pricing list can render without a local copy of the bands.
   */
  bands: readonly SubscriptionTierBand[];
}

/**
 * What a single line on a subscription invoice is charging for.
 *
 * `ReactivationFee` was removed from the API on 2026-08-07 along with the $50
 * charge for lifting a suspension; it was never built, so no historical line
 * carries it.
 */
export enum SubscriptionInvoiceLineType {
  TierCharge = 'TierCharge',
  ProductFee = 'ProductFee',
  Proration = 'Proration',
  /**
   * The payment processing fee, charged on the invoice as a whole rather than
   * on any one product. Carries no `product`, so it renders from its own
   * `description`.
   */
  ProcessingFee = 'ProcessingFee',
  Adjustment = 'Adjustment'
}

/** One charge on a subscription invoice. */
export interface SubscriptionInvoiceLine {
  id: string;
  lineType: SubscriptionInvoiceLineType;
  lineTypeDisplay: string;
  /**
   * Customer-facing description, e.g.
   * `Temporary staffing — Tier 3 (51-100 shifts)`. Display copy, and expected
   * to be reworded — read {@link usageDisplay} rather than parsing this.
   */
  description: string;
  /**
   * The Usage column. Two shapes only: the countable shift count on the tier
   * charge, and `N/A` on every other line, product fees included.
   */
  usageDisplay: string;
  preTaxAmount: number;
  /** From Stripe Tax; `0` where not taxable, or where Tax is not enabled. */
  taxAmount: number;
  /** `preTaxAmount` plus `taxAmount`. */
  totalAmount: number;

  /** Set on ProductFee and Proration lines; absent on the tier charge. */
  product?: FacilitySubscriptionProduct;
  productDisplay?: string;

  /** The part-cycle a Proration line covers; absent on every other type. */
  proratedFrom?: string;
  proratedTo?: string;
}

/**
 * A subscription invoice.
 *
 * The list route returns these without `lines`; the detail route populates it.
 */
export interface SubscriptionInvoice {
  id: string;
  facilityId: string;
  facilityName: string;
  /**
   * The facility's type, e.g. `General Hospital`. Empty when it has none —
   * `facilityTypeId` is nullable, so untyped facilities are a normal state
   * rather than missing data.
   */
  facilityTypeName: string;
  /** Shared sequence across staffing and subscription invoices. */
  invoiceNumber: string;

  cyclePeriodStart: string;
  cyclePeriodEnd: string;
  /** Pre-formatted by the API, e.g. `1–31 July 2026`. */
  cyclePeriodFormatted: string;
  generatedDate: string;
  dueDate: string;
  /** Absent while unpaid, or while an ACH payment is in flight. */
  paidDate?: string;

  /**
   * The date the facility is suspended if this stays unpaid — due date plus
   * the grace period. Absent once paid or cancelled.
   *
   * Computed by the API from the same constant its suspension job enforces,
   * so the date shown cannot drift from the date acted on, including in the
   * shortened test schedules. Neither app may derive this itself.
   */
  suspensionDeadline?: string;

  preTaxTotal: number;
  taxTotal: number;
  totalAmount: number;
  paidAmount: number;
  remainingBalance: number;

  status: InvoiceStatus;
  statusDisplay: string;
  isOverdue: boolean;
  daysOverdue: number;

  /** Shifts counted toward the tier for this cycle. */
  countableShifts: number;
  finalTierDisplay: string;
  /** Band as a display string, e.g. `51-100`, `101+`, or `—`. */
  finalTierShiftRange: string;
  tierPrice: number;

  /** Absent when the facility pays for itself. */
  payerCorporationId?: string;
  /**
   * The paying corporation's name, so the payer can be named rather than
   * merely flagged. Absent whenever `payerCorporationId` is.
   *
   * This is the corporation that *pays*, frozen on the invoice at cycle
   * close — not necessarily the facility's current owner.
   */
  payerCorporationName?: string;
  /** `Facility` or `Corporation`. */
  billedTo: string;
  invoiceGroupId?: string;

  stripeHostedInvoiceUrl?: string;
  /** Backs the download action — opened directly rather than fetched. */
  stripeInvoicePdfUrl?: string;

  /** Empty on the list route, populated on the detail route. */
  lines: SubscriptionInvoiceLine[];
}

/**
 * One shift counted toward a closed cycle's tier.
 *
 * Backs the "Included shifts timesheets" table behind an invoice's staffing
 * line. Carries no money — the shift payments sit on the staffing invoice, not
 * this one.
 */
export interface SubscriptionInvoiceShift {
  shiftId: string;
  shiftDate: string;
  contractorId: string;
  contractorName: string;

  /**
   * Absent when the contractor never clocked in. A facility-initiated late
   * cancellation still counts toward the tier, so these rows are legitimate.
   */
  clockInTime?: string;
  clockOutTime?: string;
  hoursWorked?: number;

  /**
   * Why the shift counted: `Completed` for worked shifts, `Cancelled` for the
   * facility's own late cancellations. Without it a cancelled row reads as a
   * data error.
   */
  status: string;
  statusDisplay: string;
}

/** One facility's subscription state, as listed at the corporate level. */
export interface CorporateFacilitySubscription {
  facilityId: string;
  facilityName: string;
  /**
   * Absent for facilities that have never subscribed — they are still listed,
   * so a free-tier facility stays visible to the admin who needs to subscribe
   * it.
   */
  status?: FacilitySubscriptionState;
  statusDisplay: string;
  /**
   * Whether the product is entitled *right now*. Stays true through the
   * removal window, so it cannot tell a product that is staying from one on
   * its way out — read the matching `EndsAt` for that.
   */
  directStaffingActive: boolean;
  jobPostingsActive: boolean;

  /** When the product stops billing, where removal has been scheduled. */
  directStaffingEndsAt?: string;
  jobPostingsEndsAt?: string;
  currentTier: SubscriptionTier;
  currentTierDisplay: string;
  /** Band as a display string, e.g. `51-100`, `101+`, or `—`. */
  tierShiftRange: string;
  /** Cost for the current cycle across every active product. */
  monthlyCost: number;
  currentCycleStart?: string;
  currentCycleEnd?: string;
}

/**
 * One row of the grouped cross-facility subscription list —
 * `GET api/facilities/subscription-invoices/grouped`.
 *
 * A **mixed** list, not a list of groups. A facility that pays for itself
 * stays its own `Invoice` row; the invoices a corporation pays for collapse
 * into one `Group` row per corporation per cycle. `kind` says which, and with
 * it which of the optional fields are populated.
 *
 * Branch on `kind`, never on a field being absent: the API serializes with
 * `WhenWritingNull`, so the fields belonging to the other kind are missing
 * from the JSON rather than null, and a truthiness test would also swallow a
 * legitimate zero.
 */
export interface SubscriptionInvoiceListRow {
  kind: SubscriptionInvoiceRowKind;

  /** Invoice id, or group id — what the row's detail is addressed by. */
  id: string;
  /** Invoice number, or the group's reference number. */
  reference: string;
  /** Facility name, or the paying corporation's name. */
  displayName: string;

  periodStart: string;
  periodEnd: string;
  totalAmount: number;

  /**
   * An `InvoiceStatus` on an invoice row, an `InvoiceGroupStatus` on a group
   * row. A plain string because it spans two enums: a group's three aggregate
   * values are not a subset of an invoice's eight.
   */
  status: string;

  /**
   * True when the row is overdue — the invoice itself, or any member of the
   * group. Carried separately because `InvoiceGroupStatus` has no `Overdue`:
   * overdue is a per-invoice fact, and a group only inherits it.
   */
  isOverdue: boolean;

  /** Invoice rows only. Absent on a group, which can span facility types. */
  facilityTypeName?: string;
  /** Invoice rows only. */
  facilityId?: string;

  /** Group rows only: how many facilities the cycle's group covers. */
  facilityCount?: number;
  /** Group rows only — the corporation the group detail is fetched under. */
  corporationId?: string;
}

/** Which of the two things a {@link SubscriptionInvoiceListRow} stands for. */
export enum SubscriptionInvoiceRowKind {
  Invoice = 'Invoice',
  Group = 'Group'
}

/**
 * A cycle's subscription invoices for one corporation, grouped.
 *
 * Each facility is still invoiced separately (Specification v2 §4.3); this is
 * the corporate-level view over those invoices. The API models it with the
 * same `InvoiceGroup` used for staffing, discriminated by group type — so the
 * list must ask for subscription groups explicitly, since it defaults to
 * staffing.
 */
export interface CorporateSubscriptionInvoiceGroup {
  id: string;
  /** Group-level reference shown in place of an invoice number. */
  referenceNumber: string;

  corporationId: string;
  /**
   * The owning corporation's name. Redundant in the corporate portal, which
   * already knows whose data it is showing, and there for the business app's
   * list, which spans corporations and has to name them.
   */
  corporationName: string;
  /**
   * Rolled up from the member invoices, and its own enum — a group of ten
   * facilities can be `PartiallyPaid`, which no single invoice can be.
   *
   * `PartiallyPaid` is the normal steady state for a corporation of any size:
   * Stripe charges each facility's invoice independently and failure does not
   * cascade (Specification v2 §10.4).
   */
  status: InvoiceGroupStatus;

  /** Always `Subscription` here; the same entity carries staffing groups. */
  groupType: InvoiceGroupType;

  /**
   * The cycle the group covers. Named for the weekly staffing groups the
   * entity was built for — a subscription group's period is a month.
   */
  weekStartDate: string;
  weekEndDate: string;

  /** True when any member invoice is past due. */
  hasOverdueInvoices: boolean;

  /** How many facilities the group covers. */
  invoiceCount: number;
  /** Sum of the member invoices. */
  totalAmount: number;

  createdAt: string;
  updatedAt?: string;
}

/**
 * A corporate invoice group with its member invoices.
 *
 * The group carries no due or paid date of its own — each member invoice has
 * its own, and with facilities charged independently they need not agree.
 */
export interface CorporateSubscriptionInvoiceGroupDetail extends CorporateSubscriptionInvoiceGroup {
  invoices: SubscriptionInvoiceGroupMember[];
}

/**
 * One facility's invoice inside a subscription group.
 *
 * This is `InvoiceGroupInvoiceDto` — what the shared `invoice-groups/{id}`
 * route actually returns — not a full `SubscriptionInvoice`. The two are not
 * interchangeable: the group route sends `invoiceStatus`, not `status`, and
 * carries neither `remainingBalance` nor `stripeInvoicePdfUrl`. Typing this as
 * `SubscriptionInvoice` is what let the corporate group detail read fields
 * that were never sent, and fail silently rather than at compile time.
 *
 * The three tier fields below are optional because the API does not send them
 * yet. They exist on the invoice (`countable_shifts`, `final_tier`) and on
 * `SubscriptionInvoiceDto`, but `InvoiceGroupInvoiceDto` is shared with
 * staffing groups and was never given them. Declared here so the columns that
 * need them are typed honestly and render a dash until the API catches up.
 */
export interface SubscriptionInvoiceGroupMember extends InvoiceGroupInvoiceDto {
  countableShifts?: number;
  finalTierDisplay?: string;
  finalTierShiftRange?: string;
}

/**
 * Resolves the band a shift count falls into.
 *
 * Returns `null` when the count sits below the first band's floor — a cycle
 * with no shifts carries no charge (Specification v2 §2).
 *
 * The API resolves this itself for the figures it sends; this is for the
 * widget's own projections, which move as the user's planned count changes.
 */
export function resolveSubscriptionTier(
  bands: readonly SubscriptionTierBand[],
  shifts: number
): SubscriptionTierBand | null {
  return (
    bands.find(
      (band) =>
        shifts >= band.lowerBound &&
        ((band.upperBound ?? null) === null || shifts <= band.upperBound!)
    ) ?? null
  );
}
